"use client";

import { useState, useMemo } from "react";
import { Search, Play, Filter, Youtube } from "lucide-react";
import { videos, videoCategories, type Video } from "@/lib/site-data";
import { VideoCard } from "@/components/VideoCard";
import { NewsletterSignup } from "@/components/NewsletterSignup";

export default function VideoLibraryPage() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredVideos = useMemo(() => {
    let result = [...videos];

    if (activeCategory !== "All") {
      result = result.filter((v) => v.category === activeCategory);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (v) =>
          v.title.toLowerCase().includes(q) ||
          v.description.toLowerCase().includes(q) ||
          v.category.toLowerCase().includes(q),
      );
    }

    // Featured first, then by date
    result.sort((a, b) => {
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });

    return result;
  }, [activeCategory, searchQuery]);

  const featuredVideo = filteredVideos.find((v) => v.featured);
  const gridVideos = filteredVideos.filter(
    (v) => v.id !== featuredVideo?.id,
  );

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { All: videos.length };
    for (const v of videos) {
      counts[v.category] = (counts[v.category] || 0) + 1;
    }
    return counts;
  }, []);

  return (
    <main className="min-h-screen bg-[var(--color-background)] text-[var(--color-foreground)]">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[var(--color-border)] bg-gradient-to-b from-[var(--color-surface)] to-[var(--color-background)]">
        {/* Glow effect */}
        <div className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2">
          <div className="h-[300px] w-[600px] rounded-full bg-[var(--color-primary)]/10 blur-[100px]" />
        </div>

        <div className="relative mx-auto max-w-6xl px-6 py-16 md:py-24">
          <div className="mb-4 flex items-center justify-center gap-2">
            <Youtube className="h-5 w-5 text-[var(--color-primary)]" />
            <span className="text-sm font-medium uppercase tracking-wider text-[var(--color-primary)]">
              Video Library
            </span>
          </div>

          <h1 className="mb-6 text-center text-4xl font-bold md:text-5xl">
            Learn EV Repair{" "}
            <span className="bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-accent)] bg-clip-text text-transparent">
              From the Experts
            </span>
          </h1>

          <p className="mx-auto mb-10 max-w-2xl text-center text-lg text-[var(--color-muted)]">
            Free video tutorials, case studies, and demonstrations from WFD's
            senior technical team. Start learning EV battery repair, diagnostic
            techniques, and business strategies today.
          </p>

          {/* Search bar */}
          <div className="mx-auto flex max-w-xl items-center gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-2 shadow-lg">
            <Search className="ml-3 h-5 w-5 text-[var(--color-muted)]" />
            <input
              type="text"
              placeholder="Search videos, topics, techniques..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent py-2 text-[var(--color-foreground)] placeholder:text-[var(--color-muted)] focus:outline-none"
            />
            <span className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-black">
              {filteredVideos.length} videos
            </span>
          </div>
        </div>
      </section>

      {/* Category filter */}
      <section className="border-b border-[var(--color-border)] bg-[var(--color-surface)]/30">
        <div className="mx-auto max-w-6xl px-6 py-5">
          <div className="flex flex-wrap items-center gap-2">
            <Filter className="h-4 w-4 text-[var(--color-muted)]" />
            {videoCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-all ${
                  activeCategory === cat
                    ? "bg-[var(--color-primary)] text-black shadow-[0_0_20px_rgba(0,195,122,0.3)]"
                    : "border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-muted)] hover:border-[var(--color-primary)]/50 hover:text-[var(--color-foreground)]"
                }`}
              >
                {cat}
                <span className="ml-1.5 opacity-70">
                  ({categoryCounts[cat] || 0})
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Video content */}
      <section className="mx-auto max-w-6xl px-6 py-12">
        {filteredVideos.length === 0 ? (
          <div className="py-20 text-center">
            <Play className="mx-auto mb-4 h-12 w-12 text-[var(--color-muted)] opacity-50" />
            <h3 className="mb-2 text-xl font-semibold">No videos found</h3>
            <p className="text-[var(--color-muted)]">
              Try adjusting your search or filter to find what you're looking
              for.
            </p>
          </div>
        ) : (
          <>
            {/* Featured video */}
            {featuredVideo && (
              <div className="mb-12">
                <div className="mb-4 flex items-center gap-2">
                  <span className="rounded-full bg-[var(--color-primary)]/15 px-3 py-1 text-xs font-semibold text-[var(--color-primary)]">
                    ⭐ Featured
                  </span>
                </div>
                <VideoCard video={featuredVideo} layout="featured" />
              </div>
            )}

            {/* Video grid */}
            {gridVideos.length > 0 && (
              <>
                <h2 className="mb-6 text-xl font-semibold">
                  {activeCategory === "All"
                    ? "All Videos"
                    : `${activeCategory} Videos`}
                </h2>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {gridVideos.map((video) => (
                    <VideoCard key={video.id} video={video} />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </section>

      {/* CTA Section */}
      <section className="border-t border-[var(--color-border)] bg-gradient-to-b from-[var(--color-background)] to-[var(--color-surface)]/50">
        <div className="mx-auto max-w-4xl px-6 py-16 text-center">
          <h2 className="mb-4 text-3xl font-bold">
            Want Hands-On Training?
          </h2>
          <p className="mb-8 text-[var(--color-muted)]">
            Join our intensive EV repair certification program and learn directly
            from WFD's senior engineers. 100% practical, real-world training.
          </p>
          <NewsletterSignup
            title="Get New Tutorials Weekly"
            description="Subscribe for the latest EV repair tutorials, case studies, and industry insights delivered to your inbox."
            source="video-library"
            variant="hero"
          />
        </div>
      </section>
    </main>
  );
}
